import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, useLocation, Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  isApiError,
  requiresExactCancellationRetry as requiresExactDropInCancellationRetry,
} from "@bw/shared/api";
import { useAuth, useMinuteNow } from "@bw/shared/hooks";
import type {
  DropInCancellationRequest,
  DropInCancellationResponse,
  DropInSessionResponse,
  PartyInviteResponse,
} from "@bw/shared/types/generated";
import { formatDropInSessionExpiry, formatMoney } from "@bw/shared/utils";
import { Page } from "../../../common/components/Page";
import { DetailHeader } from "../../../common/components/DetailHeader";
import { visuallyHidden } from "../../../common/visuallyHidden";
import { queryKeys } from "../../../common/query/queryClient";
import { useDropInSession } from "../hooks/useDropInSession";
import {
  useCancelDropInSession,
  useDropInCancellationDecision,
  useRecoverDropInCancellation,
} from "../hooks/useDropInCancellation";
import { useFacilityMode } from "../hooks/useFacilityMode";
import { useJoinQueue, type QueueMode } from "../hooks/useJoinQueue";
import { useCreateParty } from "../hooks/useCreateParty";
import { useJoinParty } from "../hooks/useJoinParty";
import { useMyParty } from "../hooks/useMyParty";
import { useMyQueueEntry } from "../hooks/useMyQueueEntry";
import { usePartyMemberships } from "../hooks/usePartyMemberships";
import { useQueueParty } from "../hooks/useQueueParty";
import { useKickPartyMember } from "../hooks/useKickPartyMember";
import { useDropInCourtState } from "../hooks/useDropInCourtState";
import { useDropInPaymentOptions } from "../hooks/useDropInPaymentOptions";
import { useMyPartyInvites } from "../hooks/useMyPartyInvites";
import { useRespondPartyInvite } from "../hooks/useRespondPartyInvite";
import { JoinQueuePanel } from "../components/JoinQueuePanel";
import { CheckinPassCard } from "../components/CheckinPassCard";
import { PartyInviteList } from "../components/PartyInviteList";
import { PartyMembershipRecoveryList } from "../components/PartyMembershipRecoveryList";
import { DropInCancellationDialog } from "../components/DropInCancellationDialog";
import {
  createEligibilityFocusRequest,
  PartyEligibilityPanel,
  type EligibilityFocusRequest,
} from "../components/PartyEligibilityPanel";
import { formatQueueDeadline } from "../lib/queueDeadline";
import {
  activeDropInAuthorizationExpiresAt,
  dropInSessionSnapshotIsAuthoritative,
} from "../lib/activeDropInAuthorization";
import { partyFullMessage } from "../lib/mapInviteError";
import {
  partyPlacementConflictNotice,
  type PartyPlacementNotice,
} from "../lib/partyPlacementConflict";
import { mapRespondPartyInviteError } from "../lib/mapRespondPartyInviteError";

const MAX_TIMEOUT_MS = 2_147_483_647;

/**
 * Cached session data is allowed to explain the current admission while its
 * mounted refresh is in flight, but it must stop being presented at the
 * server-owned deadline even when that request never settles. Keep this clock
 * independent from court-state authorization: those reads still require a
 * fresh mounted session response below.
 */
function useAuthorizationDeadlineRender(expiresAt: string | null) {
  const [schedule, setSchedule] = useState(0);

  useEffect(() => {
    if (expiresAt === null) return;
    const deadlineMs = Date.parse(expiresAt);
    const remainingMs = deadlineMs - Date.now();
    if (!Number.isFinite(deadlineMs) || remainingMs <= 0) return;

    const timer = window.setTimeout(
      () => setSchedule((value) => value + 1),
      Math.min(remainingMs, MAX_TIMEOUT_MS),
    );
    return () => window.clearTimeout(timer);
  }, [expiresAt, schedule]);
}

/** Mode chip + one-liner (C3) — sets the player's expectation for what happens
 * after they're in the queue / on the board. Only shown for a KNOWN mode; an
 * unknown mode keeps the fail-open (no chip). */
const MODE_CHIP: Record<string, { chip: string; line: string }> = {
  auto: {
    chip: "Auto-assign",
    line: "Courts assign automatically as they free up.",
  },
  manual: {
    chip: "Staff-run",
    line: "Staff call the next group from the queue.",
  },
  self_serve: {
    chip: "Self-serve",
    line: "Join a court line — eligible groups are assigned when the court frees up.",
  },
};

type CancellationTarget = Readonly<{
  facilityId: string;
  sessionId: string;
}>;

type CancellationAttempt = Readonly<{
  target: CancellationTarget;
  request: DropInCancellationRequest;
}>;

function targetsSameSession(
  left: CancellationTarget,
  right: CancellationTarget,
): boolean {
  return (
    left.facilityId === right.facilityId && left.sessionId === right.sessionId
  );
}

/** Human label for the payment method actually used — no invented plans. */
function paymentLabel(session: {
  payment_method: string;
  transaction_id?: string | null;
  stripe_payment_method_id?: string | null;
}): string {
  switch (session.payment_method) {
    case "single":
      return session.transaction_id || session.stripe_payment_method_id
        ? "Paid by card (single drop-in)"
        : "Free drop-in";
    case "package":
      return "1 visit pass spent";
    case "membership":
      return "Covered by your membership";
    default:
      return "Checked in";
  }
}

/** Map a join failure to honest inline copy (never a blank crash). */
function mapJoinError(err: unknown): string {
  if (isApiError(err)) {
    // ALREADY_IN_QUEUE is NOT "queue closed" (Phase C) — the caller navigates to
    // the existing entry; this copy is only the fallback if the entry_id is absent.
    if (err.code === "ALREADY_IN_QUEUE") {
      return "You're already in the queue — head to your spot in line.";
    }
    if (err.code === "NO_ACTIVE_DROP_IN") {
      return "Check in first — you need an active drop-in session to join the queue.";
    }
    if (err.code === "FACILITY_CLOSING") {
      return "There isn't enough drop-in time to join the queue. Try again when play resumes.";
    }
    if (err.code === "QUEUE_SESSION_TOO_SHORT") {
      return "Your session does not have enough time left for the minimum game. Check in again before joining the queue.";
    }
    if (err.status === 409 && /closed/i.test(err.message)) {
      return "The queue is closed right now — check back when a window is open.";
    }
  }
  return "We couldn't join you to the queue. Please try again.";
}

function cancellationSuccessNotice(result: DropInCancellationResponse): string {
  if (result.visit_restored) {
    return "Session cancelled. Your visit pass was restored.";
  }
  const issued = Number(result.refund_issued_amount);
  const target = Number(result.refund_target_amount);
  if (issued > 0 && issued < target) {
    return `Session cancelled. An additional ${formatMoney(
      result.refund_issued_amount,
      result.currency,
    )} refund was issued. Total refunds now meet ${formatMoney(
      result.refund_target_amount,
      result.currency,
    )}.`;
  }
  if (issued > 0) {
    return `Session cancelled. ${formatMoney(
      result.refund_issued_amount,
      result.currency,
    )} was refunded.`;
  }
  if (target > 0) {
    return `Session cancelled. No additional refund was needed; prior refunds already meet ${formatMoney(
      result.refund_target_amount,
      result.currency,
    )}.`;
  }
  return "Session cancelled.";
}

/**
 * Active-session HUB (/dropin/:facilityId/session).
 *
 * The post-check-in landing: shows the authoritative session expiry,
 * the payment method, the drop-in QR pass (single-use, staff-scanned), and the
 * primary "join the court queue" entries. On a successful join it advances to
 * the waiting view. A null session is honest — it never fabricates a session.
 *
 * The checked-in count is deliberately NOT shown: listing sessions requires
 * facility:read, which a player doesn't hold, so any number would be invented.
 */
export function DropInActiveSessionPage() {
  const { facilityId = "" } = useParams<{ facilityId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const now = useMinuteNow();

  // Pending staff-scan sessions use this query's foreground refresh to detect
  // validation. Active sessions keep that foreground refresh, with the final
  // interval shortened to the exact server-owned admission boundary.
  const sessionQuery = useDropInSession(facilityId, {
    refreshMode: "lifecycle",
  });
  const session = sessionQuery.data ?? null;
  const sessionMatchesRoute = session?.facility_id === facilityId;
  const sessionDoesNotMatchRoute = session !== null && !sessionMatchesRoute;
  const isCancellationRecoveryPending =
    sessionMatchesRoute && session?.cancellation_recovery_pending === true;
  const activeAuthorizationExpiresAt = activeDropInAuthorizationExpiresAt(
    session,
    facilityId,
  );
  useAuthorizationDeadlineRender(
    isCancellationRecoveryPending ? null : activeAuthorizationExpiresAt,
  );
  const isActiveSession =
    activeAuthorizationExpiresAt !== null && !isCancellationRecoveryPending;
  const isPendingValidation =
    sessionMatchesRoute &&
    session?.status === "pending_validation" &&
    !isCancellationRecoveryPending;
  const sessionIsNotPresentable =
    sessionMatchesRoute &&
    !isCancellationRecoveryPending &&
    !isActiveSession &&
    !isPendingValidation;
  const playConfigurationReadsEnabled =
    sessionQuery.isSuccess &&
    session !== null &&
    sessionMatchesRoute &&
    !isCancellationRecoveryPending;
  const queueReadsEnabled =
    sessionQuery.isSuccess && !isCancellationRecoveryPending;
  const paymentOptionsQuery = useDropInPaymentOptions(
    facilityId,
    playConfigurationReadsEnabled,
  );
  const [cancellationOpen, setCancellationOpen] = useState(false);
  const [cancellationTarget, setCancellationTarget] =
    useState<CancellationTarget | null>(null);
  const [cancellationDecisionChanged, setCancellationDecisionChanged] =
    useState(false);
  const [cancellationNotice, setCancellationNotice] = useState<string | null>(
    null,
  );
  const cancellationAttemptRef = useRef<CancellationAttempt | null>(null);
  const cancellationHookFacilityId =
    cancellationTarget?.facilityId ?? facilityId;
  const cancellationHookSessionId =
    cancellationTarget?.sessionId ?? session?.session_id ?? "";
  const cancellationDecisionQuery = useDropInCancellationDecision(
    cancellationHookFacilityId,
    cancellationHookSessionId,
    cancellationOpen || isCancellationRecoveryPending,
  );
  const cancelDropIn = useCancelDropInSession(
    cancellationTarget?.facilityId ?? facilityId,
    cancellationTarget?.sessionId ?? "",
  );
  const recoverDropInCancellation = useRecoverDropInCancellation(
    cancellationTarget?.facilityId ?? facilityId,
    cancellationTarget?.sessionId ?? "",
  );
  const incomingInvitesQuery = useMyPartyInvites(isActiveSession);
  const respondInvite = useRespondPartyInvite();
  const sessionSnapshotIsAuthoritative =
    dropInSessionSnapshotIsAuthoritative(sessionQuery);
  const courtStateIsAuthorized =
    isActiveSession && sessionSnapshotIsAuthoritative;
  const { isActorRevisionPending, isBoardRevisionPending } =
    useDropInCourtState(facilityId, 0, {
      enabled: courtStateIsAuthorized,
      authorizationExpiresAt: courtStateIsAuthorized
        ? activeAuthorizationExpiresAt
        : null,
    });

  // The facility's queue_assignment_mode decides the post-check-in path: a
  // self_serve facility sends the player to the COURT BOARD; auto/manual send
  // them to the court QUEUE.
  const facilityQuery = useFacilityMode(
    facilityId,
    playConfigurationReadsEnabled,
  );
  const sessionExpiry = formatDropInSessionExpiry(session?.expires_at, {
    now,
    timeZone: facilityQuery.data?.timezone,
  });
  const facilityTimeZone = facilityQuery.data?.timezone;
  let untilLabel: string | null = null;
  if (session?.expires_at && facilityTimeZone) {
    try {
      untilLabel = `until ${new Intl.DateTimeFormat("en-US", {
        timeZone: facilityTimeZone,
        hour: "numeric",
        minute: "2-digit",
      }).format(new Date(session.expires_at))}`;
    } catch {
      untilLabel = null;
    }
  }
  const headerSubtitle =
    [facilityQuery.data?.name, untilLabel].filter(Boolean).join(" · ") ||
    undefined;
  const join = useJoinQueue(facilityId);
  const createParty = useCreateParty(facilityId);
  const joinParty = useJoinParty(facilityId);

  // Recovery reads share the Court Board hooks and cache keys. Normal absence is
  // a successful nullable response; genuine request failures remain query errors.
  const myEntryQuery = useMyQueueEntry(facilityId, queueReadsEnabled);
  const myEntry = myEntryQuery.data ?? null;
  const queuePolicyDeadline = formatQueueDeadline(myEntry?.waiting_until);
  const queueEntrySnapshotCurrent =
    myEntryQuery.isSuccess &&
    !myEntryQuery.isFetching &&
    myEntryQuery.error === null &&
    !isActorRevisionPending;

  // The current non-terminal party restores the post-game path to a reopened
  // party. The shared court-state stream reconciles either state.
  const myPartyQuery = useMyParty(facilityId, queueReadsEnabled);
  const myParty = myPartyQuery.data ?? null;
  const queueParty = useQueueParty(myParty?.party_id ?? "", facilityId);
  const kickPartyMember = useKickPartyMember(myParty?.party_id ?? "");

  const [joinError, setJoinError] = useState<string | null>(null);
  const [joinReconciliationPending, setJoinReconciliationPending] =
    useState(false);
  const joinActionInFlight = useRef(false);
  const [partyCode, setPartyCode] = useState("");
  const [groupError, setGroupError] = useState<string | null>(null);
  const [inviteError, setInviteError] = useState<{
    inviteId: string;
    message: string;
  } | null>(null);
  const [conflictInviteId, setConflictInviteId] = useState<string | null>(null);
  const inviteAcceptRef = useRef<HTMLButtonElement>(null);
  const focusedResolvedConflictRef = useRef<string | null>(null);
  const [partyActionError, setPartyActionError] =
    useState<PartyPlacementNotice | null>(null);
  const [eligibilityFocus, setEligibilityFocus] =
    useState<EligibilityFocusRequest | null>(null);
  const eligibilityFocusSequence = useRef(0);
  const partyRecoveryRef = useRef<HTMLDivElement>(null);

  const sessionSnapshotCurrent =
    sessionSnapshotIsAuthoritative &&
    !sessionQuery.isFetching &&
    !isActorRevisionPending;
  const currentSessionDecisionIsAuthoritative =
    session !== null &&
    sessionSnapshotCurrent &&
    !cancellationDecisionQuery.isPending &&
    !cancellationDecisionQuery.isFetching &&
    cancellationDecisionQuery.error === null &&
    cancellationDecisionQuery.data?.facility_id === facilityId &&
    cancellationDecisionQuery.data.session_id === session.session_id;
  const canRecoverPendingCancellation =
    isCancellationRecoveryPending &&
    currentSessionDecisionIsAuthoritative &&
    cancellationDecisionQuery.data.recovery_pending === true;
  const cancellationTargetSnapshotCurrent =
    cancellationTarget !== null &&
    session !== null &&
    sessionSnapshotCurrent &&
    facilityId === cancellationTarget.facilityId &&
    session.facility_id === cancellationTarget.facilityId &&
    session.session_id === cancellationTarget.sessionId;
  const cancellationActionsCurrent =
    cancellationTarget !== null &&
    cancellationTargetSnapshotCurrent &&
    cancellationDecisionQuery.isSuccess &&
    !cancellationDecisionQuery.isPending &&
    !cancellationDecisionQuery.isFetching &&
    cancellationDecisionQuery.error === null &&
    cancellationDecisionQuery.data.facility_id ===
      cancellationTarget.facilityId &&
    cancellationDecisionQuery.data.session_id === cancellationTarget.sessionId;
  // Staff QR-validation gate: a `staff_check_in` facility leaves the paid player
  // in `pending_validation` until staff scan their QR (thin FE — we branch ONLY
  // on the server-owned status string, never compute the timer/validation). In
  // that state the hub shows the QR pass + a waiting message and HIDES every
  // play action; the always-on greeting + mode chip ("choose how you want to
  // play") are suppressed so the pending screen reads coherently.
  const partyMembershipsQuery = usePartyMemberships(facilityId, {
    enabled: queueReadsEnabled,
  });
  const partyMemberships = partyMembershipsQuery.data ?? [];
  const solePartyMembership =
    partyMemberships.length === 1 ? partyMemberships[0] : null;
  const inventoryHasIntegrityIssue =
    partyMemberships.length > 1 ||
    partyMemberships.some(
      (membership) => membership.integrity_reasons.length > 0,
    );
  const inventorySnapshotDisagrees =
    myParty === null
      ? partyMemberships.length !== 0
      : solePartyMembership === null ||
        solePartyMembership.party_id !== myParty.party_id ||
        solePartyMembership.facility_id !== facilityId ||
        solePartyMembership.subject_user_id !== user?.uid ||
        solePartyMembership.party_status !== myParty.status ||
        solePartyMembership.is_leader !== (myParty.viewer.role === "leader");
  const partyStateIsSettled =
    partyMembershipsQuery.isSuccess &&
    myPartyQuery.isSuccess &&
    !partyMembershipsQuery.isFetching &&
    !myPartyQuery.isFetching &&
    partyMembershipsQuery.error === null &&
    myPartyQuery.error === null &&
    !isActorRevisionPending;
  const lastSettledInventoryDisagreement = useRef<boolean | null>(null);
  useEffect(() => {
    if (partyStateIsSettled) {
      lastSettledInventoryDisagreement.current = inventorySnapshotDisagrees;
    }
  }, [inventorySnapshotDisagrees, partyStateIsSettled]);
  const inventoryDisagreesWithSingular =
    partyMembershipsQuery.isSuccess &&
    myPartyQuery.isSuccess &&
    (partyStateIsSettled
      ? inventorySnapshotDisagrees
      : (lastSettledInventoryDisagreement.current ??
        inventorySnapshotDisagrees));
  const showActivePartyRecovery =
    isActiveSession &&
    (partyMembershipsQuery.isLoading ||
      myPartyQuery.isLoading ||
      partyMembershipsQuery.error !== null ||
      myPartyQuery.error !== null ||
      inventoryHasIntegrityIssue ||
      inventoryDisagreesWithSingular);
  const partyActionsAreSafe =
    partyStateIsSettled &&
    !inventoryHasIntegrityIssue &&
    !inventoryDisagreesWithSingular;
  const incomingInvitesHaveServerSnapshot =
    incomingInvitesQuery.isSuccess &&
    incomingInvitesQuery.isFetchedAfterMount &&
    incomingInvitesQuery.error === null;
  const incomingInvites = incomingInvitesHaveServerSnapshot
    ? (incomingInvitesQuery.data ?? []).filter(
        (invite) => invite.facility_id === facilityId,
      )
    : [];
  const incomingInvitesActionsCurrent =
    incomingInvitesHaveServerSnapshot &&
    !incomingInvitesQuery.isFetching &&
    sessionSnapshotCurrent;
  const conflictInvite = conflictInviteId
    ? incomingInvites.find((invite) => invite.invite_id === conflictInviteId)
    : undefined;
  const conflictInviteMissing =
    conflictInviteId !== null &&
    incomingInvitesHaveServerSnapshot &&
    !incomingInvitesQuery.isFetching &&
    conflictInvite === undefined;
  const conflictInviteValidationUnavailable =
    conflictInviteId !== null && incomingInvitesQuery.error !== null;
  const conflictContextUsable =
    conflictInviteId !== null &&
    conflictInvite !== undefined &&
    incomingInvitesHaveServerSnapshot;
  const conflictPartyReadUnavailable =
    conflictContextUsable &&
    (partyMembershipsQuery.error !== null || myPartyQuery.error !== null);
  const conflictAlreadyResolved =
    conflictContextUsable &&
    partyStateIsSettled &&
    myParty === null &&
    partyMemberships.length === 0;
  const conflictReconciliationPending =
    conflictContextUsable &&
    !partyStateIsSettled &&
    !conflictPartyReadUnavailable;
  const inviteStatusMessage = conflictInviteMissing
    ? "This invite is no longer available."
    : conflictInviteValidationUnavailable
      ? "We couldn't confirm this invite. Please retry."
      : conflictPartyReadUnavailable
        ? "We couldn't confirm your current party."
        : conflictReconciliationPending
          ? "Checking your current party…"
          : conflictAlreadyResolved
            ? "Your previous party is no longer active. Accept this invite again."
            : inviteError?.message;
  const showIncomingInvites =
    isActiveSession &&
    (incomingInvitesQuery.isLoading ||
      incomingInvitesQuery.error !== null ||
      incomingInvites.length > 0 ||
      inviteError !== null ||
      conflictInviteId !== null);

  // Mode-driven path selection. Self_serve → court board; auto/manual → queue.
  // Cached paths remain fenced until every canonical read that selects or gates
  // them has settled, so a refresh cannot expose an action for stale server state.
  const mode = facilityQuery.data?.queue_assignment_mode;
  const isSelfServe = mode === "self_serve";
  const isQueueMode = mode === "auto" || mode === "manual";
  const facilityModeSnapshotCurrent =
    facilityQuery.isSuccess &&
    !facilityQuery.isFetching &&
    facilityQuery.error === null &&
    !isActorRevisionPending &&
    (isSelfServe || isQueueMode);
  const openPlaySnapshotCurrent =
    paymentOptionsQuery.isSuccess &&
    !paymentOptionsQuery.isFetching &&
    paymentOptionsQuery.error === null &&
    !isActorRevisionPending;
  const openPlayIsActive =
    openPlaySnapshotCurrent && paymentOptionsQuery.data.active_now;
  const playPathSnapshotCurrent =
    isActiveSession &&
    sessionSnapshotCurrent &&
    facilityModeSnapshotCurrent &&
    openPlaySnapshotCurrent &&
    queueEntrySnapshotCurrent;
  const groupActionsAreSafe = partyActionsAreSafe && playPathSnapshotCurrent;
  const partyPanelActionsAreSafe =
    isActiveSession && sessionSnapshotCurrent && partyActionsAreSafe;
  const showCourtBoard =
    playPathSnapshotCurrent &&
    isSelfServe &&
    partyActionsAreSafe &&
    myParty === null &&
    openPlayIsActive &&
    !isBoardRevisionPending;
  const showCourtBoardRecovery =
    playPathSnapshotCurrent &&
    isSelfServe &&
    partyActionsAreSafe &&
    myParty === null &&
    openPlayIsActive &&
    isBoardRevisionPending;
  const showQueue =
    playPathSnapshotCurrent &&
    isQueueMode &&
    partyActionsAreSafe &&
    myParty === null &&
    openPlayIsActive &&
    !joinReconciliationPending;
  const showOpenPlayClosed =
    playPathSnapshotCurrent &&
    !openPlayIsActive &&
    partyActionsAreSafe &&
    myParty === null &&
    myEntry === null;
  const playPathReadUnavailable =
    sessionQuery.error !== null ||
    facilityQuery.error !== null ||
    myEntryQuery.error !== null ||
    paymentOptionsQuery.error !== null;
  const partyCourtBoardPending =
    partyPanelActionsAreSafe &&
    isBoardRevisionPending &&
    myParty?.eligibility.action === "choose_court";

  useEffect(() => {
    if (queueEntrySnapshotCurrent && myEntry !== null) {
      join.clearAttempt();
    }
  }, [
    join.clearAttempt,
    myEntry?.entry_id,
    myEntry?.mode,
    queueEntrySnapshotCurrent,
  ]);

  // "Join with a friend" after a kiosk check-in lands on #sec-group: bring the
  // party controls into view once they render.
  const { hash } = useLocation();
  const groupSectionShown = groupActionsAreSafe && myParty === null;
  const scrolledToGroup = useRef(false);
  useEffect(() => {
    if (hash !== "#sec-group" || !groupSectionShown || scrolledToGroup.current) return;
    scrolledToGroup.current = true;
    document.getElementById("sec-group")?.scrollIntoView({ block: "start" });
  }, [groupSectionShown, hash]);

  useEffect(() => {
    if (eligibilityFocus?.target !== "recovery" || myParty !== null) {
      return;
    }
    partyRecoveryRef.current?.focus();
    partyRecoveryRef.current?.scrollIntoView?.({ block: "nearest" });
  }, [eligibilityFocus, myParty]);

  useEffect(() => {
    if (!conflictInviteMissing || !conflictInviteId) return;
    setInviteError({
      inviteId: conflictInviteId,
      message: "This invite is no longer available.",
    });
    setConflictInviteId(null);
  }, [conflictInviteId, conflictInviteMissing]);

  useEffect(() => {
    if (!conflictAlreadyResolved || !incomingInvitesActionsCurrent) {
      if (!conflictAlreadyResolved) {
        focusedResolvedConflictRef.current = null;
      }
      return;
    }
    if (focusedResolvedConflictRef.current === conflictInviteId) return;
    focusedResolvedConflictRef.current = conflictInviteId;
    inviteAcceptRef.current?.focus();
  }, [
    conflictAlreadyResolved,
    conflictInviteId,
    incomingInvitesActionsCurrent,
  ]);

  function retryPartyState() {
    void Promise.all([myPartyQuery.refetch(), partyMembershipsQuery.refetch()]);
  }

  function retryPlayPathState() {
    void Promise.all([
      sessionQuery.refetch(),
      facilityQuery.refetch(),
      myEntryQuery.refetch(),
      paymentOptionsQuery.refetch(),
    ]);
  }

  function openCancellation() {
    if (
      cancellationOpen ||
      !session ||
      !sessionSnapshotCurrent ||
      session.facility_id !== facilityId ||
      (isCancellationRecoveryPending && !canRecoverPendingCancellation)
    ) {
      return;
    }
    const target: CancellationTarget = {
      facilityId,
      sessionId: session.session_id,
    };
    cancellationAttemptRef.current = null;
    setCancellationDecisionChanged(false);
    setCancellationNotice(null);
    cancelDropIn.reset();
    recoverDropInCancellation.reset();
    setCancellationTarget(target);
    setCancellationOpen(true);
  }

  function closeCancellation() {
    if (
      cancelDropIn.isPending ||
      recoverDropInCancellation.isPending ||
      requiresExactDropInCancellationRetry(cancelDropIn.error)
    ) {
      return;
    }
    cancellationAttemptRef.current = null;
    setCancellationDecisionChanged(false);
    cancelDropIn.reset();
    recoverDropInCancellation.reset();
    setCancellationOpen(false);
    setCancellationTarget(null);
  }

  function completeCancellation(
    target: CancellationTarget,
    result: DropInCancellationResponse,
  ) {
    cancellationAttemptRef.current = null;
    setCancellationOpen(false);
    setCancellationTarget(null);
    setCancellationNotice(cancellationSuccessNotice(result));
    queryClient.setQueryData<DropInSessionResponse | null>(
      queryKeys.dropin.session(target.facilityId),
      (cachedSession) =>
        cachedSession?.session_id === target.sessionId ? null : cachedSession,
    );
    void queryClient.invalidateQueries({
      queryKey: queryKeys.dropin.all,
    });
  }

  function confirmCancellation() {
    const target = cancellationTarget;
    if (
      !target ||
      !cancellationActionsCurrent ||
      !cancellationDecisionQuery.data?.allowed ||
      cancelDropIn.isPending
    ) {
      return;
    }
    const previousAttempt = cancellationAttemptRef.current;
    const request =
      previousAttempt && targetsSameSession(previousAttempt.target, target)
        ? previousAttempt.request
        : { idempotency_key: globalThis.crypto.randomUUID() };
    cancellationAttemptRef.current = { target, request };
    setCancellationDecisionChanged(false);
    cancelDropIn.mutate(request, {
      onSuccess: (result) => completeCancellation(target, result),
      onError: (error) => {
        if (requiresExactDropInCancellationRetry(error)) {
          return;
        }
        cancellationAttemptRef.current = null;
        if (isApiError(error) && error.status === 409) {
          setCancellationDecisionChanged(true);
          void cancellationDecisionQuery.refetch();
        }
      },
    });
  }

  function continueCancellation() {
    const target = cancellationTarget;
    if (
      !target ||
      !cancellationActionsCurrent ||
      !cancellationDecisionQuery.data?.recovery_pending ||
      recoverDropInCancellation.isPending
    ) {
      return;
    }
    recoverDropInCancellation.mutate(undefined, {
      onSuccess: (result) => completeCancellation(target, result),
    });
  }

  async function handleJoin(mode: QueueMode) {
    if (
      !playPathSnapshotCurrent ||
      !isQueueMode ||
      !openPlayIsActive ||
      joinActionInFlight.current
    ) {
      return;
    }
    joinActionInFlight.current = true;
    setJoinReconciliationPending(true);
    setJoinError(null);
    try {
      const entry = await join.mutateAsync(mode);
      navigate(`/dropin/${facilityId}/queue/${entry.entry_id}`);
    } catch (err) {
      // A failed POST is ambiguous until the caller-owned canonical read says
      // whether the transaction committed.
      const canonical = await myEntryQuery.refetch({ cancelRefetch: true });
      if (canonical.isSuccess && canonical.data) {
        join.clearAttempt();
        navigate(`/dropin/${facilityId}/queue/${canonical.data.entry_id}`);
        return;
      }

      // Preserve the existing structured-conflict recovery as a fallback when
      // the canonical read completed successfully but returned no active row.
      if (
        canonical.isSuccess &&
        isApiError(err) &&
        err.code === "ALREADY_IN_QUEUE"
      ) {
        const existingEntryId = (
          err.details as { entry_id?: string } | undefined
        )?.entry_id;
        if (existingEntryId) {
          join.clearAttempt(mode);
          navigate(`/dropin/${facilityId}/queue/${existingEntryId}`);
          return;
        }
      }

      if (canonical.isSuccess) {
        setJoinError(mapJoinError(err));
      }
    } finally {
      joinActionInFlight.current = false;
      setJoinReconciliationPending(false);
    }
  }

  function handleCreateParty() {
    if (!groupActionsAreSafe) return;
    setGroupError(null);
    createParty.mutate(undefined, {
      onSuccess: (party) => {
        navigate(`/dropin/party/${party.party_id}`);
      },
      onError: () =>
        setGroupError("We couldn't create a party. Please try again."),
    });
  }

  function handleJoinParty() {
    if (!groupActionsAreSafe) return;
    const code = partyCode.trim();
    if (!code) return;
    setGroupError(null);
    joinParty.mutate(code, {
      onSuccess: (party) => {
        navigate(`/dropin/party/${party.party_id}`);
      },
      onError: (err) => {
        if (isApiError(err) && err.status === 404) {
          setGroupError(
            "No open party found for that code. Check it and try again.",
          );
          return;
        }
        if (isApiError(err) && err.code === "PARTY_FULL") {
          setGroupError(partyFullMessage(err.details));
          return;
        }
        setGroupError("We couldn't join that party. Please try again.");
      },
    });
  }

  function handleRespondInvite(invite: PartyInviteResponse, accept: boolean) {
    if (!incomingInvitesActionsCurrent || respondInvite.isPending) return;
    setInviteError((current) =>
      current?.inviteId === invite.invite_id ? null : current,
    );
    if (!accept && conflictInviteId === invite.invite_id) {
      setConflictInviteId(null);
    }
    respondInvite.mutate(
      {
        inviteId: invite.invite_id,
        facilityId: invite.facility_id,
        accept,
      },
      {
        onSuccess: () => {
          setConflictInviteId((current) =>
            current === invite.invite_id ? null : current,
          );
          setInviteError((current) =>
            current?.inviteId === invite.invite_id ? null : current,
          );
        },
        onError: (err) => {
          const hasMembershipConflict =
            isApiError(err) && err.code === "ALREADY_IN_PARTY";
          setInviteError({
            inviteId: invite.invite_id,
            message: mapRespondPartyInviteError(err, accept),
          });
          setConflictInviteId((current) =>
            hasMembershipConflict
              ? invite.invite_id
              : current === invite.invite_id
                ? null
                : current,
          );
        },
      },
    );
  }

  function handlePlaceParty() {
    if (
      !myParty ||
      !myParty.viewer.can_place ||
      !partyPanelActionsAreSafe ||
      !myParty.eligibility.leader_can_place ||
      (myParty.eligibility.action !== "join_auto_queue" &&
        myParty.eligibility.action !== "join_staff_queue")
    ) {
      return;
    }

    const previousEligibility = myParty.eligibility;
    setPartyActionError(null);
    queueParty.mutate(
      { mode: "normal" },
      {
        onSuccess: (entry) => {
          navigate(`/dropin/${facilityId}/queue/${entry.entry_id}`);
        },
        onError: async (err) => {
          if (isApiError(err) && err.status === 409) {
            const conflictNotice = partyPlacementConflictNotice(err);
            const [partyResult, entryResult, membershipsResult] =
              await Promise.all([
                myPartyQuery.refetch(),
                myEntryQuery.refetch(),
                partyMembershipsQuery.refetch(),
              ]);
            const nextEligibility = partyResult.isSuccess
              ? partyResult.data?.eligibility
              : previousEligibility;
            setEligibilityFocus(
              createEligibilityFocusRequest(
                ++eligibilityFocusSequence.current,
                previousEligibility,
                nextEligibility,
              ),
            );
            if (
              partyResult.isSuccess &&
              entryResult.isSuccess &&
              membershipsResult.isSuccess
            ) {
              setPartyActionError(conflictNotice);
            } else {
              setPartyActionError({
                ...conflictNotice,
                message: `${conflictNotice.message} We couldn't refresh every party status.`,
              });
            }
            return;
          }
          setPartyActionError({
            message: "We couldn't place the party. Please try again.",
          });
        },
      },
    );
  }

  function handleRemovePartyMember(targetMemberId: string) {
    if (
      !myParty ||
      myParty.status !== "open" ||
      !myParty.viewer.can_remove_members ||
      !partyPanelActionsAreSafe
    ) {
      return;
    }
    setPartyActionError(null);
    kickPartyMember.mutate(targetMemberId, {
      onSuccess: () => {
        void Promise.all([
          myPartyQuery.refetch(),
          partyMembershipsQuery.refetch(),
        ]);
      },
      onError: () =>
        setPartyActionError({
          message: "We couldn't remove that member. Please try again.",
        }),
    });
  }

  // ---- honest no-session state -------------------------------------------
  if (
    !sessionQuery.isLoading &&
    (sessionDoesNotMatchRoute ||
      sessionIsNotPresentable ||
      (sessionQuery.error === null && session === null))
  ) {
    const memberships = partyMembershipsQuery.data ?? [];
    const canCheckIn =
      partyActionsAreSafe && myParty === null && memberships.length === 0;

    return (
      <Page>
        <Stack spacing={2} sx={{ alignItems: "flex-start" }}>
          {cancellationNotice ? (
            <Alert severity="success" role="status" sx={{ width: "100%" }}>
              {cancellationNotice}
            </Alert>
          ) : null}
          <Box sx={{ width: "100%" }}>
            <DetailHeader
              title="Drop-in session"
              titleComponent="p"
              subtitle={facilityQuery.data?.name}
              back={{ to: `/facility/${facilityId}`, label: "Back to facility" }}
            />
            <Typography component="h1" sx={visuallyHidden}>
              No active drop-in session
            </Typography>
            <Typography color="text.secondary" variant="body2" sx={{ mt: 1 }}>
              Pay for your spot to join the court queue.
            </Typography>
          </Box>
          <PartyMembershipRecoveryList
            facilityId={facilityId}
            authenticatedUserId={user?.uid}
            memberships={partyMembershipsQuery.data}
            loading={partyMembershipsQuery.isLoading || myPartyQuery.isLoading}
            error={
              partyMembershipsQuery.error !== null ||
              myPartyQuery.error !== null
            }
            onRetry={retryPartyState}
            showEmptyMismatch={inventoryDisagreesWithSingular}
          />
          {canCheckIn ? (
            <Button
              component={Link}
              to={`/dropin/${facilityId}`}
              variant="contained"
              size="small"
            >
              Check in
            </Button>
          ) : null}
        </Stack>
      </Page>
    );
  }

  return (
    <Page>
      <Stack
        spacing={3}
        sx={{ "& .MuiButton-root.Mui-disabled": { pointerEvents: "auto" } }}
      >
        {/* Greeting + mode chip are the ACTIVE-hub header. They read "choose how
          you want to play", which contradicts the pending state, so both are
          suppressed while awaiting staff validation (the pending branch renders
          its own header). */}
        {isCancellationRecoveryPending ? (
          <Box>
            <Typography component="h1" variant="h4">
              {canRecoverPendingCancellation
                ? "Finish your cancellation"
                : "Cancellation in progress"}
            </Typography>
            <Typography color="text.secondary" sx={{ mt: 0.5 }}>
              Your admission is paused while this cancellation is reconciled.
            </Typography>
          </Box>
        ) : !isPendingValidation && session !== null ? (
          <>
            <Box>
              <DetailHeader
                title="Drop-in session"
                titleComponent="p"
                subtitle={headerSubtitle}
                back={{ to: `/facility/${facilityId}`, label: "Back to facility" }}
              />
              <Typography component="h1" sx={visuallyHidden}>
                You&apos;re checked in
              </Typography>
              <Typography color="text.secondary" variant="body2" sx={{ mt: 1 }}>
                You&apos;re in — choose how you want to play.
              </Typography>
            </Box>

            {/* Mode chip + one-liner (C3): what happens next at THIS facility. Only for a
              known mode — an unknown/failed mode read keeps the fail-open (no chip). */}
            {mode && MODE_CHIP[mode] ? (
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1}
                role="note"
                sx={{ alignItems: { sm: "center" } }}
              >
                <Chip
                  size="small"
                  color="primary"
                  variant="outlined"
                  label={MODE_CHIP[mode].chip}
                />
                <Typography variant="body2" color="text.secondary">
                  {MODE_CHIP[mode].line}
                </Typography>
              </Stack>
            ) : null}
          </>
        ) : null}

        {!isCancellationRecoveryPending && playPathReadUnavailable ? (
          <Alert severity="error">
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1}
              sx={{ alignItems: { sm: "center" } }}
            >
              <Typography sx={{ flex: 1 }}>
                We couldn&apos;t verify your current play options. Actions are
                paused until your status is current.
              </Typography>
              <Button
                type="button"
                variant="outlined"
                size="small"
                onClick={retryPlayPathState}
              >
                Retry play status
              </Button>
            </Stack>
          </Alert>
        ) : null}

        {sessionQuery.isLoading ? (
          <LoadingStatus>Loading your session…</LoadingStatus>
        ) : session ? (
          isCancellationRecoveryPending ? (
            <Stack spacing={1.5} sx={{ alignItems: "flex-start" }}>
              <Alert severity="info" role="status" sx={{ width: "100%" }}>
                Your admission is paused while the cancellation finishes.
              </Alert>
              {cancellationDecisionQuery.isPending ||
              cancellationDecisionQuery.isFetching ? (
                <LoadingStatus>Checking cancellation status…</LoadingStatus>
              ) : cancellationDecisionQuery.isError ? (
                <Alert severity="error">
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={1}
                    sx={{ alignItems: { sm: "center" } }}
                  >
                    <Typography sx={{ flex: 1 }}>
                      We couldn&apos;t load the cancellation status.
                    </Typography>
                    <Button
                      type="button"
                      variant="outlined"
                      size="small"
                      onClick={() => void cancellationDecisionQuery.refetch()}
                    >
                      Retry
                    </Button>
                  </Stack>
                </Alert>
              ) : canRecoverPendingCancellation ? (
                <Button
                  type="button"
                  variant="contained"
                  size="small"
                  disabled={!canRecoverPendingCancellation}
                  onClick={openCancellation}
                >
                  Continue cancellation
                </Button>
              ) : (
                <Alert severity="info" role="status" sx={{ width: "100%" }}>
                  Facility staff must finish this cancellation.
                </Alert>
              )}
            </Stack>
          ) : isPendingValidation ? (
            /* Staff QR-validation gate: paid, awaiting a staff scan. Show the QR
             pass + a waiting message; every play action stays hidden until the
             scan flips the session to `active` (server-side). */
            <Stack spacing={2} sx={{ alignItems: "flex-start" }}>
              <Box>
                <Typography component="h1" variant="h4">
                  Waiting for staff to check you in
                </Typography>
                <Typography color="text.secondary" sx={{ mt: 0.5 }}>
                  You&apos;re checked in and paid. Show this pass to a staff
                  member — they&apos;ll scan it to start your session.
                </Typography>
              </Box>
              <CheckinPassCard facilityId={facilityId} />
              <Button
                type="button"
                color="error"
                variant="outlined"
                size="small"
                disabled={!sessionSnapshotCurrent}
                onClick={openCancellation}
              >
                Cancel session
              </Button>
              <PartyMembershipRecoveryList
                facilityId={facilityId}
                authenticatedUserId={user?.uid}
                memberships={partyMembershipsQuery.data}
                loading={
                  partyMembershipsQuery.isLoading || myPartyQuery.isLoading
                }
                error={
                  partyMembershipsQuery.error !== null ||
                  myPartyQuery.error !== null
                }
                onRetry={retryPartyState}
                showEmptyMismatch={inventoryDisagreesWithSingular}
              />
            </Stack>
          ) : (
            <Stack spacing={2}>
              <SrStatus>Session active.</SrStatus>
              {/* Session facts: backend expiry + the real payment method. */}
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Stack spacing={1.5} sx={{ alignItems: "flex-start" }}>
                  <Typography variant="overline" color="primary.main">
                    Session
                  </Typography>
                  <Typography sx={{ fontWeight: 700 }}>
                    Your drop-in session is active.
                  </Typography>
                  <Stack component="ul" spacing={0.5} sx={{ m: 0, pl: 2.5 }}>
                    <Typography
                      component="li"
                      variant="body2"
                      color="text.secondary"
                    >
                      <time dateTime={session.expires_at ?? undefined}>
                        {sessionExpiry ?? "Session end time unavailable"}
                      </time>
                    </Typography>
                    <Typography
                      component="li"
                      variant="body2"
                      color="text.secondary"
                    >
                      {paymentLabel(session)}
                    </Typography>
                    {session.display_name ? (
                      <Typography
                        component="li"
                        variant="body2"
                        color="text.secondary"
                      >
                        {session.display_name}
                      </Typography>
                    ) : null}
                  </Stack>
                  <Button
                    type="button"
                    color="error"
                    variant="outlined"
                    size="small"
                    disabled={!sessionSnapshotCurrent}
                    onClick={openCancellation}
                  >
                    Cancel session
                  </Button>
                </Stack>
              </Paper>

              {showIncomingInvites ? (
                <Box
                  component="section"
                  aria-labelledby="sec-party-invites"
                  aria-busy={incomingInvitesQuery.isFetching || undefined}
                >
                  <Typography variant="overline" color="primary.main">
                    Together
                  </Typography>
                  <Typography
                    component="h2"
                    variant="h5"
                    id="sec-party-invites"
                  >
                    Party invitations
                  </Typography>
                  <Stack spacing={1.5} sx={{ mt: 1.5 }}>
                    {incomingInvitesQuery.isLoading ? (
                      <Typography
                        color="text.secondary"
                        role="status"
                        aria-live="polite"
                      >
                        Loading party invitations…
                      </Typography>
                    ) : null}
                    {incomingInvitesQuery.error !== null ? (
                      <Stack spacing={1} sx={{ alignItems: "flex-start" }}>
                        <Alert severity="error">
                          We couldn&apos;t load your party invitations.
                        </Alert>
                        <Button
                          type="button"
                          variant="outlined"
                          size="small"
                          onClick={() => void incomingInvitesQuery.refetch()}
                        >
                          Retry
                        </Button>
                      </Stack>
                    ) : null}
                    {inviteStatusMessage ? (
                      <Alert
                        severity={
                          conflictReconciliationPending ? "info" : "error"
                        }
                        role={
                          conflictReconciliationPending ? "status" : "alert"
                        }
                        aria-live="polite"
                      >
                        {inviteStatusMessage}
                      </Alert>
                    ) : null}
                    {incomingInvites.length > 0 ? (
                      <PartyInviteList
                        mode="respond"
                        invites={incomingInvites}
                        actionsDisabled={
                          respondInvite.isPending ||
                          !incomingInvitesActionsCurrent
                        }
                        acceptButtonRef={inviteAcceptRef}
                        acceptButtonRefInviteId={conflictInviteId ?? undefined}
                        onRespond={handleRespondInvite}
                      />
                    ) : null}
                  </Stack>
                </Box>
              ) : null}

              {conflictContextUsable && !conflictAlreadyResolved ? (
                <PartyMembershipRecoveryList
                  facilityId={facilityId}
                  authenticatedUserId={user?.uid}
                  memberships={partyMembershipsQuery.data}
                  loading={
                    partyMemberships.length === 0 &&
                    conflictReconciliationPending
                  }
                  error={conflictPartyReadUnavailable}
                  onRetry={retryPartyState}
                  showViewParty
                  cleanupAllowed={
                    incomingInvitesActionsCurrent && partyStateIsSettled
                  }
                />
              ) : null}

              {showActivePartyRecovery && !conflictContextUsable ? (
                <PartyMembershipRecoveryList
                  facilityId={facilityId}
                  authenticatedUserId={user?.uid}
                  memberships={partyMembershipsQuery.data}
                  loading={
                    partyMembershipsQuery.isLoading || myPartyQuery.isLoading
                  }
                  error={
                    partyMembershipsQuery.error !== null ||
                    myPartyQuery.error !== null
                  }
                  onRetry={retryPartyState}
                  showEmptyMismatch={inventoryDisagreesWithSingular}
                />
              ) : null}

              {/* Frictionless drop-in (2026-07-02): the single-use drop-in QR pass card
              was removed — paying already checked the player in, so there is nothing
              to scan at the desk. The hub leads straight to the play actions. */}

              {/* Self-serve COURT BOARD entry (Phase 2). Party members use the
              party-scoped action below so they cannot bypass leader-controlled
              placement through the solo board path. */}
              {showCourtBoard ? (
                <Box component="section" aria-labelledby="sec-board">
                  <SectionHeading eyebrow="Drop-in" id="sec-board">
                    Find a court
                  </SectionHeading>
                  <Typography color="text.secondary" sx={{ mt: 1 }}>
                    See court status, join a court line, or view games in
                    progress.
                  </Typography>
                  <Button
                    component={Link}
                    to={`/dropin/${facilityId}/court-board`}
                    variant="contained"
                    size="small"
                    sx={{ mt: 1.5 }}
                  >
                    Court board
                  </Button>
                </Box>
              ) : null}
              {showCourtBoardRecovery ? (
                <Typography
                  color="text.secondary"
                  role="status"
                  aria-label="Court status"
                  aria-live="polite"
                >
                  Refreshing court status. Court selection will return when the
                  latest update is ready.
                </Typography>
              ) : null}
              {showOpenPlayClosed ? (
                <Box component="section" aria-labelledby="sec-open-play">
                  <SectionHeading eyebrow="Drop-in" id="sec-open-play">
                    Drop-in is closed
                  </SectionHeading>
                  <Typography
                    color="text.secondary"
                    role="status"
                    sx={{ mt: 1 }}
                  >
                    Play options will return when drop-in resumes.
                  </Typography>
                </Box>
              ) : null}

              {/* C2 recovery: the caller already has an ACTIVE entry — show a
              "You're in line" card linking back to it, REPLACING the Join CTA (a
              second join would 409). */}
              {joinReconciliationPending && myEntry === null ? (
                <Box component="section" aria-labelledby="sec-join-status">
                  <SectionHeading eyebrow="Drop-in" id="sec-join-status">
                    Confirming your queue spot
                  </SectionHeading>
                  <Typography
                    color="text.secondary"
                    role="status"
                    aria-live="polite"
                    sx={{ mt: 1 }}
                  >
                    Checking the latest queue status…
                  </Typography>
                </Box>
              ) : myEntry ? (
                <Box component="section" aria-labelledby="sec-recover">
                  <SectionHeading eyebrow="Drop-in" id="sec-recover">
                    You&apos;re in line
                  </SectionHeading>
                  <Paper
                    variant="outlined"
                    role="status"
                    sx={{
                      mt: 1.5,
                      p: 2,
                      borderRadius: "14px",
                      borderColor: "primary.main",
                      textAlign: "center",
                    }}
                  >
                    <Stack spacing={1.5} sx={{ alignItems: "center" }}>
                      <Typography sx={{ fontWeight: 700 }}>
                        {!queueEntrySnapshotCurrent &&
                        myEntry.status === "assigned"
                          ? `Last confirmed update included a court assignment${myEntry.court_name ? ` — ${myEntry.court_name}` : ""}. Refresh to verify it before going to the court.`
                          : myEntry.status === "assigned"
                            ? `Your court is ready${myEntry.court_name ? ` — ${myEntry.court_name}` : ""}. Head over and check in.`
                            : `${myEntry.position != null ? `You're #${myEntry.position} in the queue. ` : "You're waiting in the queue. "}${queuePolicyDeadline ? `Auto-assignment by ${queuePolicyDeadline}.` : myEntry.estimated_wait_minutes != null ? `Estimated wait ~${myEntry.estimated_wait_minutes} min (approximate).` : ""}`}
                      </Typography>
                      {!queueEntrySnapshotCurrent ? (
                        <Typography variant="body2" color="text.secondary">
                          {myEntry.status === "assigned"
                            ? "Court assignments can change while status is unavailable."
                            : "Last confirmed queue status. Refresh to verify your current position before taking another action."}
                        </Typography>
                      ) : null}
                      {queueEntrySnapshotCurrent ? (
                        <Button
                          component={Link}
                          to={`/dropin/${facilityId}/queue/${myEntry.entry_id}`}
                          variant="contained"
                          size="small"
                        >
                          View your spot
                        </Button>
                      ) : null}
                    </Stack>
                  </Paper>
                </Box>
              ) : showQueue ? (
                /* Join the court queue (auto/manual modes) — only when there's no
               existing entry to recover. */
                <Box component="section" aria-labelledby="sec-join">
                  <SectionHeading eyebrow="Drop-in" id="sec-join">
                    Join the court queue
                  </SectionHeading>
                  {joinError ? (
                    <Alert severity="error" sx={{ mt: 1.5 }}>
                      {joinError}
                    </Alert>
                  ) : null}
                  <Box sx={{ mt: 1.5 }}>
                    <JoinQueuePanel
                      onJoin={(mode) => void handleJoin(mode)}
                      pending={join.isPending || joinReconciliationPending}
                    />
                  </Box>
                </Box>
              ) : null}

              {partyActionError ? (
                <Alert
                  severity="error"
                  data-conflict-code={partyActionError.conflictCode}
                >
                  {partyActionError.message}
                </Alert>
              ) : null}

              {eligibilityFocus?.target === "recovery" && myParty === null ? (
                <Alert
                  ref={partyRecoveryRef}
                  severity="info"
                  role="status"
                  tabIndex={-1}
                >
                  Your party is no longer active. Your current play options have
                  been refreshed.
                </Alert>
              ) : null}

              {myParty && !conflictContextUsable ? (
                <PartyEligibilityPanel
                  eligibility={myParty.eligibility}
                  canPlaceParty={myParty.viewer.can_place}
                  canRemoveMembers={myParty.viewer.can_remove_members}
                  canViewActivity={myParty.viewer.can_leave}
                  actionsDisabled={!partyPanelActionsAreSafe}
                  showPartyAction={!partyCourtBoardPending}
                  actionPending={queueParty.isPending}
                  removePending={kickPartyMember.isPending}
                  activityHref={
                    myParty.entry_id
                      ? `/dropin/${facilityId}/queue/${myParty.entry_id}`
                      : undefined
                  }
                  partyHref={`/dropin/party/${myParty.party_id}`}
                  title="Your party"
                  focusRequest={eligibilityFocus}
                  onPlaceParty={handlePlaceParty}
                  onRemoveMember={
                    myParty.status === "open"
                      ? handleRemovePartyMember
                      : undefined
                  }
                />
              ) : null}
              {partyCourtBoardPending ? (
                <Typography
                  color="text.secondary"
                  role="status"
                  aria-label="Court status"
                  aria-live="polite"
                >
                  Refreshing court status. Court selection will return when the
                  latest update is ready.
                </Typography>
              ) : null}

              {/* Play as a group: create a party or join one by code. */}
              {groupActionsAreSafe && myParty === null ? (
                <Box component="section" aria-labelledby="sec-group">
                  <SectionHeading eyebrow="Together" id="sec-group">
                    Play as a group
                  </SectionHeading>
                  {groupError ? (
                    <Alert severity="error" sx={{ mt: 1.5 }}>
                      {groupError}
                    </Alert>
                  ) : null}
                  <Stack
                    spacing={1.5}
                    sx={{ mt: 1.5, alignItems: "flex-start" }}
                  >
                    <Button
                      type="button"
                      variant="contained"
                      size="small"
                      onClick={handleCreateParty}
                      disabled={createParty.isPending || !groupActionsAreSafe}
                    >
                      Create a party
                    </Button>
                    <Typography color="text.secondary">
                      Already have a code from a friend? Enter it to join their
                      party.
                    </Typography>
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={1}
                      sx={{ width: "100%", alignItems: { sm: "flex-start" } }}
                    >
                      <TextField
                        id="party-code"
                        type="text"
                        label="Party code"
                        size="small"
                        value={partyCode}
                        onChange={(e) => setPartyCode(e.target.value)}
                        autoComplete="off"
                        autoCapitalize="characters"
                        disabled={!groupActionsAreSafe}
                      />
                      <Button
                        type="button"
                        variant="outlined"
                        size="small"
                        onClick={handleJoinParty}
                        disabled={
                          joinParty.isPending ||
                          partyCode.trim() === "" ||
                          !groupActionsAreSafe
                        }
                      >
                        Join with a code
                      </Button>
                    </Stack>
                  </Stack>
                </Box>
              ) : null}
            </Stack>
          )
        ) : null}

        {cancellationOpen && cancellationTarget ? (
          <DropInCancellationDialog
            decision={cancellationDecisionQuery.data}
            decisionLoading={
              cancellationDecisionQuery.isPending ||
              cancellationDecisionQuery.isFetching
            }
            decisionError={cancellationDecisionQuery.error}
            actionsDisabled={!cancellationActionsCurrent}
            cancelPending={cancelDropIn.isPending}
            cancelError={cancelDropIn.error}
            recoveryPending={recoverDropInCancellation.isPending}
            recoveryError={recoverDropInCancellation.error}
            decisionChanged={cancellationDecisionChanged}
            onDecisionRetry={() => void cancellationDecisionQuery.refetch()}
            onConfirm={confirmCancellation}
            onRecover={continueCancellation}
            onClose={closeCancellation}
          />
        ) : null}
      </Stack>
    </Page>
  );
}

function LoadingStatus({ children }: { children: string }) {
  return (
    <Stack
      direction="row"
      spacing={1}
      role="status"
      aria-live="polite"
      sx={{ alignItems: "center" }}
    >
      <CircularProgress size={18} />
      <Typography color="text.secondary">{children}</Typography>
    </Stack>
  );
}

function SrStatus({ children }: { children: string }) {
  return (
    <Box
      role="status"
      sx={{
        position: "absolute",
        width: "1px",
        height: "1px",
        p: 0,
        m: "-1px",
        overflow: "hidden",
        clip: "rect(0 0 0 0)",
        whiteSpace: "nowrap",
        border: 0,
      }}
    >
      {children}
    </Box>
  );
}

function SectionHeading({
  eyebrow,
  id,
  children,
}: {
  eyebrow: string;
  id: string;
  children: string;
}) {
  return (
    <Box>
      <Typography variant="overline" color="primary.main">
        {eyebrow}
      </Typography>
      <Typography component="h2" variant="h5" id={id}>
        {children}
      </Typography>
    </Box>
  );
}
